import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ClipboardList,
  PencilRuler,
  Hammer,
  Flame,
  SprayCan,
  Sparkles,
  Cog,
  Eye,
  Gauge,
  Package,
  HardHat,
} from "lucide-react";

/* =====================================================================
   STEPS — Each step has an icon, title and EITHER `image` (real photo)
   OR `placeholder = true` (blueprint card). Future swap:
     1) Drop the real photo into /app/frontend/public/
     2) Add `image: "/<filename>"` and remove `placeholder`
     3) Layout & animations stay untouched.
   ===================================================================== */
const STEPS = [
  { id: 1,  title: "Customer Requirement",       icon: ClipboardList, image: "/process/step-01-customer-requirement.png" },
  { id: 2,  title: "Engineering & Design",       icon: PencilRuler,   image: "/process/step-02-engineering-design.png" },
  { id: 3,  title: "Precision Fabrication",      icon: Hammer,        image: "/process/step-03-precision-fabrication.png" },
  { id: 4,  title: "Welding & Assembly",         icon: Flame,         image: "/process/step-04-welding-assembly.png" },
  { id: 5,  title: "Surface Preparation",        icon: SprayCan,      image: "/process/step-05-surface-preparation.png" },
  { id: 6,  title: "Premium Powder Coating",     icon: Sparkles,      image: "/process/step-06-powder-coating.png" },
  { id: 7,  title: "Component Assembly",         icon: Cog,           image: "/process/step-07-component-assembly.png" },
  { id: 8,  title: "Owner Quality Inspection",   icon: Eye,           placeholder: true },
  { id: 9,  title: "Performance Testing",        icon: Gauge,         image: "/process/step-09-performance-testing.png" },
  { id: 10, title: "Packaging & Dispatch",       icon: Package,       image: "/process/step-10-packaging-dispatch.png" },
  // Real LIFTILE on-site installation photograph for Step 11.
  { id: 11, title: "Installation & Customer Support", icon: HardHat,  image: "/process/step-11-installation-support.png" },
];

/* ---------- Step Card ---------- */
function StepCard({ step, index }) {
  const Icon = step.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: "easeOut", delay: index * 0.06 }}
      data-testid={`mfg-step-${step.id}`}
      className="group relative shrink-0 w-[300px] md:w-[320px] snap-center"
    >
      {/* Step number badge (above card) */}
      <div className="flex items-center gap-3 mb-4">
        <div className="font-heading text-[10px] uppercase tracking-[0.32em] text-liftile-orange font-semibold">
          Step {String(step.id).padStart(2, "0")}
        </div>
        <div className="flex-1 h-px bg-liftile-cream/15" />
      </div>

      {/* Image / placeholder square */}
      <div className="relative aspect-square w-full overflow-hidden rounded-xl bg-gradient-to-br from-[#181818] via-[#0d0d0d] to-[#000] border border-white/[0.05]">
        {step.image ? (
          <>
            <img
              src={step.image}
              alt={step.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-liftile-black/55 via-transparent to-transparent" />
          </>
        ) : (
          <>
            {/* Blueprint grid texture */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.10]"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                backgroundSize: "32px 32px",
              }}
            />
            {/* Orange ambient glow corner */}
            <div
              aria-hidden
              className="absolute -bottom-16 -right-16 w-[220px] h-[220px] rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, rgba(255,107,26,0.30) 0%, rgba(255,107,26,0) 70%)",
                filter: "blur(30px)",
              }}
            />
            {/* Big icon centered */}
            <div className="relative h-full grid place-items-center transition-transform duration-700 ease-out group-hover:scale-105">
              <div className="relative">
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full bg-liftile-orange/15 blur-2xl"
                />
                <div className="relative grid place-items-center w-24 h-24 md:w-28 md:h-28 rounded-full border border-liftile-orange/50">
                  <Icon className="w-10 h-10 md:w-12 md:h-12 text-liftile-orange" strokeWidth={1.4} />
                </div>
              </div>
            </div>
            {/* Bottom corner tag */}
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
              <span className="font-heading text-[9px] uppercase tracking-[0.32em] text-liftile-cream/45">
                LIFTILE · Process
              </span>
              {step.awaitsRealPhoto && (
                <span
                  data-testid={`mfg-step-${step.id}-pending-badge`}
                  className="font-heading text-[9px] uppercase tracking-[0.22em] text-liftile-orange/85 border border-liftile-orange/45 px-2 py-1 rounded-sm"
                >
                  Real Photo Soon
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Title */}
      <h3 className="mt-5 font-heading uppercase text-base md:text-lg text-liftile-cream tracking-tight leading-snug">
        {step.title}
      </h3>
    </motion.div>
  );
}

/* ---------- Main Section ---------- */
export default function ManufacturingExcellence() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 0.85", "end 0.2"],
  });
  // Top orange line that "draws itself" left→right as user scrolls through
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      ref={sectionRef}
      data-testid="about-manufacturing"
      className="relative bg-liftile-black py-24 md:py-32 overflow-hidden"
    >
      {/* Thin orange top accent + soft ambient blobs */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30" />
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none opacity-30"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.4) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(50px)",
        }}
      />

      <div className="container-x relative">
        {/* Header */}
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="heading-eyebrow mb-4">Process</div>
            <h2
              data-testid="about-mfg-heading"
              className="font-display uppercase text-liftile-cream leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl"
            >
              Manufacturing <span className="text-liftile-orange">Excellence</span>
            </h2>
            <p className="font-body text-liftile-cream/65 mt-6 text-base md:text-lg leading-relaxed max-w-2xl">
              Every LIFTILE machine follows a structured engineering process
              designed to ensure reliability, safety and long-term performance.
            </p>
          </motion.div>
        </div>

        {/* Self-drawing orange progress line */}
        <div className="relative mt-14 md:mt-20">
          <div className="relative h-px bg-liftile-cream/12">
            <motion.div
              aria-hidden
              style={{ scaleX: lineScale, transformOrigin: "left" }}
              className="absolute inset-0 h-px bg-liftile-orange"
            />
            {/* End marker dot */}
            <motion.div
              aria-hidden
              style={{ x: useTransform(lineScale, (v) => `${v * 100}%`) }}
              className="absolute -top-1.5 w-3 h-3 rounded-full bg-liftile-orange shadow-[0_0_12px_2px_rgba(255,107,26,0.7)]"
            />
          </div>

          {/* Horizontal scroll rail */}
          <div
            data-testid="mfg-timeline-rail"
            className="mt-10 -mx-4 px-4 overflow-x-auto snap-x snap-mandatory scrollbar-thin scrollbar-thumb-liftile-orange/40 scrollbar-track-transparent"
            style={{ scrollbarColor: "rgba(255,107,26,0.4) transparent" }}
          >
            <div className="flex items-start gap-7 md:gap-9 pb-8">
              {STEPS.map((step, i) => (
                <StepCard key={step.id} step={step} index={i} />
              ))}
              {/* Trailing breathing room */}
              <div aria-hidden className="shrink-0 w-2" />
            </div>
          </div>

          {/* Drag-to-explore hint */}
          <div className="mt-2 flex items-center justify-center md:justify-end gap-2 text-liftile-cream/40 font-heading text-[10px] uppercase tracking-[0.32em]">
            <span>← Drag to explore the process →</span>
          </div>
        </div>
      </div>
    </section>
  );
}
